import { useState, useCallback, useEffect } from 'react';
import { useReactFlow } from 'reactflow';

import { Image as ChakraImage, SimpleGrid, Stack } from '@chakra-ui/react';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import type { GifsResult } from '@giphy/js-fetch-api';
import type { IGif } from '@giphy/js-types';
import { JSONContent } from '@tiptap/core';
import { proxy, useSnapshot } from 'valtio';
import { assignPersonaId, insertMediaIntoNode } from '../../tiptap/contentJSONs';
import { useTenantContext } from '../../../../TenantContext';
import { AnchoredPanel, AnchorRect } from './AnchoredPanel';

// Global so the picker can be opened from any of the add-block entry points
// (FlowActionMenu, BlockConnectMenu, NodeOutgoingMenu) and insert into whichever
// node is live. Only the open state, the target node and the anchor position
// are shared; the search term and results are local to the panel.
class GifMenuState {
  isOpen = false;
  targetNodeId = null as string | null;
  anchorRect = null as AnchorRect | null;

  open() {
    this.isOpen = true;
  }
  close() {
    this.isOpen = false;
    this.targetNodeId = null;
    this.anchorRect = null;
  }
}

export const gifMenuState = proxy(new GifMenuState());

const PANEL_WIDTH = 360;
const ITEMS_PER_PAGE = 10;

const giphySearch = async (
  apiKey: string,
  searchTerm: string,
  {
    offset,
    limit,
  }: {
    offset: number;
    limit: number;
  },
) => {
  const data = await fetch(
    `https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${searchTerm}&limit=${limit}&offset=${offset}`,
  );
  const parsed = await data.json();

  return parsed as GifsResult;
};

const giphyTrending = async (
  apiKey: string,
  { offset, limit }: { offset: number; limit: number },
) => {
  const gifs = await fetch(
    `https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=${limit}&offset=${offset}`,
  );

  const data = await gifs.json();
  return data as GifsResult;
};

const createJSONContent = (gif: IGif): JSONContent => {
  return {
    type: 'resizableMedia',
    attrs: {
      src: gif.images.original.url,
      alt: gif.alt_text ?? '',
      height: '150px',
      width: '300px',
      title: gif.title,
      'media-type': 'gif',
    },
  };
};

export function GifModal() {
  const tenantCtx = useTenantContext();
  const giphyApiKey = tenantCtx?.settings.GIPHY_API_KEY ?? '';
  const { setNodes } = useReactFlow();

  const state = useSnapshot(gifMenuState);
  const { isOpen, anchorRect } = state;

  const [searchTerm, setSearchTerm] = useState('');
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchGifs = useCallback(
    async (nextOffset: number) => {
      if (searchTerm) {
        return giphySearch(giphyApiKey, searchTerm, { offset: nextOffset, limit: ITEMS_PER_PAGE });
      }
      return giphyTrending(giphyApiKey, { offset: nextOffset, limit: ITEMS_PER_PAGE });
    },
    [searchTerm, giphyApiKey],
  );

  // Load results whenever the panel opens or the (debounced) search term
  // changes: trending when empty, search results otherwise. Reset on close.
  useEffect(() => {
    if (!isOpen) {
      setGifs([]);
      setOffset(0);
      return;
    }
    let cancelled = false;
    setOffset(0);
    fetchGifs(0).then(({ data }) => {
      if (!cancelled) {
        setGifs(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, fetchGifs]);

  // Clear the search term when the panel closes so the next open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const loadMore = useCallback(async () => {
    if (loadingMore) {
      return;
    }
    setLoadingMore(true);
    const nextOffset = offset + ITEMS_PER_PAGE;
    const { data } = await fetchGifs(nextOffset);
    setOffset(nextOffset);
    // Giphy can repeat an id across pages; dedupe so React keys stay unique.
    setGifs((current) => {
      const seen = new Set(current.map((g) => g.id));
      return [...current, ...data.filter((g) => !seen.has(g.id))];
    });
    setLoadingMore(false);
  }, [loadingMore, offset, fetchGifs]);

  // Infinite scroll: fetch the next page when the grid is scrolled near the end.
  const onGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      loadMore();
    }
  };

  const selectGif = (gif: IGif) => {
    const targetNodeId = gifMenuState.targetNodeId;
    if (!targetNodeId) {
      throw new Error('No target node id');
    }

    const block = assignPersonaId(createJSONContent(gif));

    if (insertMediaIntoNode(setNodes, targetNodeId, block)) {
      gifMenuState.close();
      return;
    }

    // The target node isn't the live (selected) one, so it has no mounted
    // editor. Select it so its editor mounts, then insert on the next frame.
    setNodes((nodes) => nodes.map((node) => ({ ...node, selected: node.id === targetNodeId })));
    requestAnimationFrame(() => {
      insertMediaIntoNode(setNodes, targetNodeId, block);
      gifMenuState.close();
    });
  };

  return (
    <AnchoredPanel
      isOpen={isOpen}
      onClose={() => gifMenuState.close()}
      anchorRect={anchorRect ?? null}
      width={PANEL_WIDTH}
      title="GIF"
    >
      <Stack spacing={2}>
        <SearchInput urlSync={false} placeholder="Search for GIFs..." onCommit={setSearchTerm} />
        {gifs.length > 0 ? (
          <SimpleGrid
            columns={2}
            spacing="10px"
            maxH={pxToRem(320)}
            overflowY="auto"
            onScroll={onGridScroll}
          >
            {gifs.map((gif) => (
              <ChakraImage
                key={gif.id}
                src={gif.images.original.url}
                onClick={() => selectGif(gif)}
                cursor="pointer"
              />
            ))}
          </SimpleGrid>
        ) : null}
      </Stack>
    </AnchoredPanel>
  );
}
